import type {
  MinimalDevice,
  NetworkInterface,
} from '@rtkwlf/asset-data-service';
import dayjs from 'dayjs';
import { t } from 'i18next';

import { getMaxLastSuccessfulScan } from '../../get-max-last-successful-scan/get-max-last-successful-scan';

const MULTI_VALUE_SEPARATOR = ', ';
const DEFAULT_FIELD_VALUE = '';

const categoryAccessor = (row: MinimalDevice) => {
  const category = row.category;
  if (!category) {
    return DEFAULT_FIELD_VALUE;
  }
  return t(`ads:category:${category}`);
};

const tagsAccessor = (row: MinimalDevice) => {
  const tags = row.tags;
  if (!tags) return DEFAULT_FIELD_VALUE;
  return tags
    .map((tag) => {
      if (!tag) return DEFAULT_FIELD_VALUE;
      return tag;
    })
    .filter(Boolean)
    .join(MULTI_VALUE_SEPARATOR);
};

const ipAddressAccessor = (row: MinimalDevice) => {
  const ipAddresses =
    row.networkInterfaces
      ?.map((networkDetails: NetworkInterface) => networkDetails.ipAddresses)
      .flat()
      .join(MULTI_VALUE_SEPARATOR) ?? DEFAULT_FIELD_VALUE;

  return ipAddresses;
};

const sourcesAccessor = (row: MinimalDevice) => {
  const sources = row.sources;
  if (!sources) return DEFAULT_FIELD_VALUE;
  return sources
    .map((source) => {
      if (!source) return DEFAULT_FIELD_VALUE;
      return t(`ads:source:${source}`);
    })
    .join(MULTI_VALUE_SEPARATOR);
};

const macAddressAccessor = (row: MinimalDevice) => {
  const macAddresses =
    row.networkInterfaces
      ?.map((networkDetails: NetworkInterface) => networkDetails.mac)
      .flat()
      .join(MULTI_VALUE_SEPARATOR) ?? DEFAULT_FIELD_VALUE;

  return macAddresses;
};

const operatingSystemAccessor = (row: MinimalDevice) => {
  const osName = row.os?.name;
  const osVersion = row.os?.version;
  if (!osName && !osVersion) return DEFAULT_FIELD_VALUE;

  return [
    osName && `${t('ads:assetDetails.information.osDetails.name')} ${osName}`,
    osVersion &&
      `${t('ads:assetDetails.information.osDetails.version')} ${osVersion}`,
  ]
    .filter(Boolean)
    .join(MULTI_VALUE_SEPARATOR);
};

const riskScoreAccessor = (row: MinimalDevice) => {
  const riskScore = row.risk?.score;

  if (!riskScore && typeof riskScore !== 'number') {
    return DEFAULT_FIELD_VALUE;
  }
  return riskScore.toFixed(1).toString();
};

const importanceAccessor = (row: MinimalDevice) => {
  const importance = row.importance;
  if (!importance) {
    return DEFAULT_FIELD_VALUE;
  }
  return t(`ads:importance:${importance}`);
};

const lastSuccessfulScanAccessor = (row: MinimalDevice) => {
  const lastSuccessfulScan = getMaxLastSuccessfulScan(
    row.risk?.lastSuccessfulScanned,
  );
  if (!lastSuccessfulScan) return DEFAULT_FIELD_VALUE;
  return dayjs(lastSuccessfulScan).utc().toISOString();
};

const lastSeenTimeAccessor = (row: MinimalDevice) => {
  const lastSeenTime = row.lastSeenTime;
  if (!lastSeenTime) return DEFAULT_FIELD_VALUE;
  return dayjs(lastSeenTime).utc().toISOString();
};

export const CSV_COLUMN_FIELD_ACCESSOR: Record<
  string,
  string | ((row: MinimalDevice) => string)
> = {
  [t('ads:fields.deviceName')]: 'name',
  [t('ads:fields.category')]: categoryAccessor,
  [t('ads:fields.tags')]: tagsAccessor,
  [t('ads:fields.sources')]: sourcesAccessor,
  [t('ads:fields.importance')]: importanceAccessor,
  [t('ads:fields.lastSuccessfulScan')]: lastSuccessfulScanAccessor,
  [t('ads:fields.lastSeen')]: lastSeenTimeAccessor,
  [t('ads:fields.ipAddress')]: ipAddressAccessor,
  [t('ads:fields.macAddress')]: macAddressAccessor,
  [t('ads:fields.riskCount')]: 'risk.count',
  [t('ads:fields.riskScore')]: riskScoreAccessor,
  [t('ads:fields.hostname')]: 'hostname',
  [t('ads:fields.netbios')]: 'netbios',
  [t('ads:fields.os')]: operatingSystemAccessor,
  [t('ads:fields.osType')]: 'os.type',
};

export const DEFAULT_COLUMNS = [
  t('ads:fields.deviceName'),
  t('ads:fields.category'),
  t('ads:fields.tags'),
  t('ads:fields.sources'),
  t('ads:fields.importance'),
  t('ads:fields.lastSuccessfulScan'),
  t('ads:fields.lastSeen'),
  t('ads:fields.ipAddress'),
  t('ads:fields.macAddress'),
];

// TODO: Hide Risk score and Risk count based on the MR_IN_UP1 feature flag.
// Ticket:https://arcticwolf.atlassian.net/browse/S2CX-18093
export const RISK_ADDITIONS_COLUMNS = [
  t('ads:fields.riskCount'),
  t('ads:fields.riskScore'),
];

export const OPTIONAL_COLUMNS = [
  t('ads:fields.hostname'),
  t('ads:fields.netbios'),
  t('ads:fields.os'),
];

export const ADS_FULL_MOCKED_COLUMNS = [t('ads:fields.osType')];
